import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, ShieldCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';

export default function RegulatorPortal() {
  const [searchParams] = useSearchParams();
  const accessCode = searchParams.get('code') || '';

  const [code, setCode] = useState(accessCode);
  const [verified, setVerified] = useState(false);
  const [regulator, setRegulator] = useState<any>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  async function verify() {
    if (!code) return toast.error('Please enter an access code');
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('verify_regulator_access', {
        p_access_code: code,
      });
      if (error) throw error;
      if (!data || data.length === 0) throw new Error('Invalid or expired access code');

      setRegulator(data[0]);
      setVerified(true);
      toast.success('Access granted');

      // Load summaries for jurisdiction
      const { data: summaries, error: summariesError } = await supabase.rpc(
        'list_audit_summaries',
        {
          p_jurisdiction: data[0].jurisdiction,
        }
      );
      if (summariesError) throw summariesError;
      setRows(summaries || []);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (accessCode) verify();
  }, []);

  if (!verified) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="text-primary" />
              Regulator Portal Access
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Enter your regulator access code to view audit summaries.
            </p>
            <Input
              placeholder="Access code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && verify()}
            />
            <Button onClick={verify} disabled={loading} className="w-full">
              {loading ? <Loader2 className="animate-spin h-4 w-4" /> : 'Verify Access'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Welcome, {regulator?.regulator_name}</CardTitle>
            <p className="text-sm text-muted-foreground">
              Jurisdiction: {regulator?.jurisdiction} | Access expires:{' '}
              {new Date(regulator?.expires_at).toLocaleDateString()}
            </p>
          </CardHeader>
        </Card>

        {/* Overview Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Trust Scores Overview</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows.slice(0, 20)}>
                <XAxis dataKey="company_name" hide />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Bar dataKey="trust_score" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Statistics */}
        <div className="grid sm:grid-cols-3 gap-3">
          <Card>
            <CardHeader className="pb-1">
              <CardTitle className="text-sm">Total Companies</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold">{rows.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-1">
              <CardTitle className="text-sm">Avg Trust Score</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold">
                {rows.length > 0
                  ? Math.round(rows.reduce((sum, c) => sum + (c.trust_score || 0), 0) / rows.length)
                  : 0}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-1">
              <CardTitle className="text-sm">Companies w/ Flags</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold">
                {rows.filter((c) => (c.flags || 0) > 0).length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Companies Table */}
        <Card>
          <CardHeader>
            <CardTitle>Audit Summaries</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Last Audit</TableHead>
                  <TableHead>Trust Score</TableHead>
                  <TableHead>Proof Coverage</TableHead>
                  <TableHead>Flags</TableHead>
                  <TableHead>Risk Score</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.company_id}>
                    <TableCell className="font-medium">{r.company_name}</TableCell>
                    <TableCell>
                      {r.last_audit_at ? new Date(r.last_audit_at).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell>{r.trust_score ?? '—'}</TableCell>
                    <TableCell>{r.proof_coverage ? `${r.proof_coverage}%` : '—'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {r.flags ?? 0}
                        {r.flags > 0 && <AlertTriangle className="text-amber-500 h-4 w-4" />}
                      </div>
                    </TableCell>
                    <TableCell>{r.risk_score ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
